// Return Unused Meds — a nurse sends a patient's leftover medication back to
// the pharmacy. Self-explanatory by design (ward-return-flow-SPEC §3): one line
// per drug, a quantity capped at what the ward still holds, a reason, one
// button. Everything else — batches, stores, item ids, which dispense it came
// from, splitting a return across batches — is resolved here and never shown.
//
// v1 caps at ward_held (received − already-returned); it does not yet subtract
// administered doses (needs the pharmacy→order link, SPEC §1a). The nurse enters
// the physical leftover they're returning, which the pharmacy re-validates on
// acknowledge. The bill credit happens later when the pharmacy acknowledges
// (SPEC §3) — the nurse is not involved in billing.
import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack,
  TextField, MenuItem, Typography, LinearProgress,
} from "@mui/material";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { getReturnableItems, createWardReturn, listOf } from "../api/nurseApi";

const REASONS = [
  "Course changed / discontinued",
  "Patient discharged",
  "Excess dispensed",
  "Patient refused",
  "Other",
];
const num = (v) => (v == null || v === "" ? 0 : Number(v));

// Collapse the per-item+batch returnable rows into ONE line per drug — the
// nurse sees a drug, not batches. The underlying rows are kept for allocation.
function groupByDrug(rows) {
  const map = new Map();
  for (const r of rows) {
    const key = r.item_id;
    if (!map.has(key)) {
      map.set(key, { item_id: r.item_id, item_name: r.item_name || r.item_id, returnable: 0, rows: [] });
    }
    const g = map.get(key);
    g.returnable += num(r.ward_held);
    g.rows.push(r);
  }
  return [...map.values()];
}

export default function ReturnMedsDialog({ open, onClose, admissionId, patientId, patientName, onReturned }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState({}); // item_id -> { qty, reason }
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(() => {
    if (!open) return;
    setLoading(true);
    getReturnableItems(admissionId, patientId)
      .then((r) => {
        const g = groupByDrug(listOf(r));
        setGroups(g);
        // Deliberately NOT defaulted to the full returnable figure. ward_held is
        // a ceiling, not a suggestion: it does not subtract doses already
        // administered, so pre-filling it would offer 5 back on a course where
        // only 3 are physically left — contradicting the ward-stock count the
        // Medications tab now shows. The nurse enters the physical count.
        const f = {};
        g.forEach((x) => { f[x.item_id] = { qty: "", reason: "" }; });
        setForm(f);
      })
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, [open, admissionId, patientId]);
  useEffect(reload, [reload]);

  const setField = (itemId, key, value) =>
    setForm((f) => ({ ...f, [itemId]: { ...f[itemId], [key]: value } }));

  const active = groups.filter((g) => num(form[g.item_id]?.qty) > 0);
  const overCap = active.some((g) => num(form[g.item_id].qty) > g.returnable);
  const missingReason = active.some((g) => !form[g.item_id].reason);
  const blocked = submitting || active.length === 0 || overCap || missingReason;

  const submit = async () => {
    setSubmitting(true);
    try {
      // Allocate each drug's requested quantity across its item+batch rows,
      // bucketed by (source dispense, store) — a ward-return is per dispense, so
      // a drug spanning two dispenses becomes two returns (the common single-
      // dispense case is one). Each row is one item+batch, so the batch the
      // quantity came off is carried through on the line (largest holding first).
      const buckets = new Map();
      for (const g of active) {
        let remaining = num(form[g.item_id].qty);
        const remark = form[g.item_id].reason;
        const rows = [...g.rows].sort((a, b) => num(b.ward_held) - num(a.ward_held));
        for (const row of rows) {
          if (remaining <= 0) break;
          const take = Math.min(remaining, num(row.ward_held));
          if (take <= 0) continue;
          const key = `${row.source_transaction_id}::${row.from_store_id}`;
          if (!buckets.has(key)) {
            buckets.set(key, {
              // The dispense's UUID (returnable's source_transaction_id), which
              // the return names as source_reference_id — not the ORD-… number.
              source_reference_id: row.source_transaction_id,
              to_store_id: row.from_store_id,
              items: [],
            });
          }
          buckets.get(key).items.push({
            item_id: row.item_id,
            uom_id: row.uom_id,
            batch_number: row.batch_number,
            requested_quantity: String(take),
            unit_rate: row.unit_rate,
            remarks: remark,
          });
          remaining -= take;
        }
      }

      const payloads = [...buckets.values()].map((b) => ({
        // Pharmacy validates these as strings — coerce (admission_id is a number
        // EMR-side). transaction_number (RET-…) is assigned by the EMR proxy.
        patient_id: String(patientId),
        admission_id: String(admissionId),
        return_type: "WARD_RETURN",
        source_reference_id: b.source_reference_id,
        to_store_id: b.to_store_id,
        items: b.items,
      }));

      const results = await Promise.all(
        payloads.map((p) =>
          createWardReturn(p).catch((e) => ({ status: "Failed", message: e?.response?.data?.message }))
        )
      );
      const failed = results.filter((r) => r?.status !== "Success");
      if (failed.length === 0) {
        toast.success("Return sent to pharmacy.");
        onReturned?.();
        onClose();
      } else {
        // A 200-Failed rides its own message; surface the first and refresh so
        // the returnable figures reflect anything that did go through.
        toast.error(failed[0]?.message || "Some returns could not be sent.");
        reload();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NURSE_COLORS.primary }}>
        Return Unused Meds{patientName ? ` — ${patientName}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <LinearProgress />
        ) : groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            Nothing to return — this patient has no medicines left on the ward.
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Only medicines still held on the ward are shown. Count what you're
              physically sending back and enter it — the figure shown is the most
              you can return, not a suggestion.
            </Typography>
            {groups.map((g) => {
              const qty = form[g.item_id]?.qty ?? "";
              const over = num(qty) > g.returnable;
              return (
                <Box key={g.item_id} sx={{ borderBottom: `1px solid ${NURSE_COLORS.border}`, pb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.item_name}</Typography>
                  <Typography variant="caption" color="text.secondary">Up to {g.returnable} returnable</Typography>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                    <TextField
                      size="small" type="number" label="Return" sx={{ width: 110 }}
                      inputProps={{ min: 0, max: g.returnable }} error={over}
                      value={qty}
                      onChange={(e) => setField(g.item_id, "qty", e.target.value)}
                    />
                    <TextField
                      select size="small" label="Reason" sx={{ flex: 1 }}
                      disabled={num(qty) <= 0}
                      error={num(qty) > 0 && !form[g.item_id]?.reason}
                      value={form[g.item_id]?.reason || ""}
                      onChange={(e) => setField(g.item_id, "reason", e.target.value)}
                    >
                      {REASONS.map((r) => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
                    </TextField>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} variant="outlined">Cancel</Button>
        <Button onClick={submit} disabled={blocked} variant="contained">
          {submitting ? "Sending…" : "Return to Pharmacy"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
